import { inject, Injectable } from '@angular/core';
import { DocumentType } from '@softapps/wws/pos-core';
import {
  addYears,
  ApiDataAccess,
  DateService,
  ResponseResult,
  startOfYear,
} from '@softline/core';
import { Observable } from 'rxjs';
import {
  SOFTLINE_API_CASH_REGISTER_DOCUMENT_FIND,
  SOFTLINE_API_CASH_REGISTER_DOCUMENT_GET_POSITIONS,
  SOFTLINE_API_CASH_REGISTER_DOCUMENT_PRINT,
  SOFTLINE_API_CASH_REGISTER_DOCUMENT_PRINT_LAST_RECEIPT,
  SOFTLINE_API_CASH_REGISTER_DOCUMENT_SCAN,
} from '../main.api';

@Injectable()
export class DocumentRepository {
  readonly #dataAccess = inject(ApiDataAccess);
  readonly dateService = inject(DateService);

  find(
    documentType: DocumentType,
    documentNumber: string,
    salesOrganisationId: number
  ): Observable<unknown[]> {
    return this.#dataAccess.get$({
      path: SOFTLINE_API_CASH_REGISTER_DOCUMENT_FIND,
      pathParams: { documentType },
      queryParams: {
        documentNumber: documentNumber ?? '',
        salesOrganisationId,
        fromDate: startOfYear(addYears(this.dateService.now(), -5)),
        toDate: this.dateService.now(),
      },
    });
  }

  async scan(
    type: string,
    code: string
  ): Promise<{
    id: number;
    barcode: string;
    beleg: string;
    belegname: string;
    belegnummer: number;
  }> {
    return (
      await this.#dataAccess.get<
        ResponseResult<{
          id: number;
          barcode: string;
          beleg: string;
          belegname: string;
          belegnummer: number;
        }>
      >({
        path: SOFTLINE_API_CASH_REGISTER_DOCUMENT_SCAN,
        pathParams: { type, code },
      })
    ).result;
  }

  getPositions(documentType: DocumentType, documentId: number): Observable<unknown[]> {
    return this.#dataAccess.get$({
      path: SOFTLINE_API_CASH_REGISTER_DOCUMENT_GET_POSITIONS,
      pathParams: { documentType, documentId },
    });
  }

  print(cartId: number, documentType: DocumentType, copies: number): Promise<unknown> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_DOCUMENT_PRINT,
        pathParams: {
          documentType: documentType.toUpperCase(),
          cartId,
        },
      },
      {
        copies,
      }
    );
  }

  async printLastReceipt(copies: number): Promise<void> {
    await this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_DOCUMENT_PRINT_LAST_RECEIPT,
      },
      {
        copies,
      }
    );
  }
}
